import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Stack, Alert, CircularProgress,
  TextField, MenuItem, FormControlLabel, Checkbox, FormGroup, Avatar, Switch,
  ToggleButtonGroup, ToggleButton, Chip, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { DataGrid } from '@mui/x-data-grid';
import {
  getShowsV2Api, addShowV2Api, editShowV2Api, deleteShowV2Api, getChannelsV2Api,
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// ─── Grid constants ───────────────────────────────────────────────────────────
const HOUR_HEIGHT = 64;                          // px per hour (1 px ≈ 0.9375 min)
const PX_PER_MIN  = HOUR_HEIGHT / 60;
const TIME_COL_W  = 56;
const START_HOUR  = 1;
const END_HOUR    = 24;
const TOTAL_HOURS = END_HOUR - START_HOUR;       // 23
const GRID_HEIGHT = TOTAL_HOURS * HOUR_HEIGHT + HOUR_HEIGHT / 2; // 1504 px (+30 min tail for midnight row
const SNAP_MINS   = 30;

const HOUR_LINES = Array.from({ length: TOTAL_HOURS + 1 }, (_, i) => START_HOUR + i);
const HALF_LINES = Array.from({ length: TOTAL_HOURS + 1 }, (_, i) => START_HOUR + i + 0.5); // includes 12:30am

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const RECURRENCE_TYPES = ['once', 'daily'];

const CARD_COLORS = [
  '#1565C0', '#00695C', '#6A1B9A', '#AD1457',
  '#BF360C', '#1B5E20', '#E65100', '#0277BD',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const timeToMins = (t) => {
  if (!t) return START_HOUR * 60;
  const [h, m] = t.split(':').map(Number);
  return (h === 0 ? 24 : h) * 60 + m; // 00:00 → 24:00
};

const minsToTimeStr = (mins) => {
  const clamped = Math.max(0, Math.min(24 * 60, mins));
  const h = Math.floor(clamped / 60) % 24;
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const minsToY = (mins) => (mins - START_HOUR * 60) * PX_PER_MIN;

const snap   = (m) => Math.round(m / SNAP_MINS) * SNAP_MINS;
const clamp  = (m) => Math.max(START_HOUR * 60, Math.min(END_HOUR * 60, m));

const formatHourLabel = (h) => {
  if (h === 24) return '12 AM';
  if (h === 12) return '12 PM';
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
};

const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const hr = h % 12 || 12;
  return m === 0 ? `${hr}${h < 12 ? 'am' : 'pm'}` : `${hr}:${String(m).padStart(2, '0')}${h < 12 ? 'am' : 'pm'}`;
};

const hashColor = (str) => {
  if (!str) return CARD_COLORS[0];
  let v = 0;
  for (let i = 0; i < str.length; i++) v = str.charCodeAt(i) + ((v << 5) - v);
  return CARD_COLORS[Math.abs(v) % CARD_COLORS.length];
};

const isMultiDay = (show) =>
  show?.recurrence?.type === 'daily' || (show?.recurrence?.days?.length ?? 0) > 1;

function getShowDays(show) {
  const type = show.recurrence?.type || 'weekly';
  if (type === 'daily') return DAYS;
  if (type === 'special') {
    if (show.specialDate) return [DAYS[new Date(show.specialDate).getDay()]];
    return [];
  }
  if (show.recurrence?.days?.length) return show.recurrence.days;
  if (show.dayOfWeek) return [show.dayOfWeek];
  return [];
}

// ─── Overlap layout ───────────────────────────────────────────────────────────
function computeIndentLevels(shows) {
  if (shows.length <= 1) return new Map(shows.map((s) => [s._id, 0]));

  const getRange = (show) => {
    const s = timeToMins(show.timeStart);
    let e = timeToMins(show.timeEnd);
    if (e <= s) e = END_HOUR * 60;
    return [s, e];
  };

  const overlaps = (a, b) => {
    const [as, ae] = getRange(a);
    const [bs, be] = getRange(b);
    return as < be && ae > bs;
  };

  // Longer duration → lower indent level (sits behind)
  const sorted = [...shows].sort((a, b) => {
    const [as, ae] = getRange(a);
    const [bs, be] = getRange(b);
    return (be - bs) - (ae - as); // ascending duration = shorter first in array after sort
  }).reverse(); // longest first

  const levels = new Array(sorted.length).fill(0);
  for (let i = 1; i < sorted.length; i++) {
    for (let j = 0; j < i; j++) {
      if (overlaps(sorted[i], sorted[j])) {
        levels[i] = Math.max(levels[i], levels[j] + 1);
      }
    }
  }

  return new Map(sorted.map((show, i) => [show._id, levels[i]]));
}

// ─── Ghost card (drop preview) ────────────────────────────────────────────────
function GhostCard({ startMins, endMins, color }) {
  const top    = minsToY(Math.max(startMins, START_HOUR * 60));
  const bottom = minsToY(Math.min(endMins,   END_HOUR   * 60));
  const height = Math.max(bottom - top, 30);

  return (
    <Box sx={{
      position: 'absolute', top, left: 2, right: 2, height,
      bgcolor: color, opacity: 0.45,
      border: '2px dashed rgba(255,255,255,0.85)',
      borderRadius: '4px', zIndex: 6, pointerEvents: 'none',
    }} />
  );
}

// ─── Show card ────────────────────────────────────────────────────────────────
const HANDLE_H = 8; // px for top/bottom resize handles

const INDENT_PX = 20;

function ShowCard({ show, indentLevel = 0, isDragging, onMoveStart, onResizeTopStart, onResizeBottomStart, onClick }) {
  const startMins = timeToMins(show.timeStart);
  let   endMins   = timeToMins(show.timeEnd);
  if (endMins <= startMins) endMins = END_HOUR * 60;

  const gridStart = START_HOUR * 60;
  const gridEnd   = END_HOUR   * 60;
  if (startMins >= gridEnd || endMins <= gridStart) return null;

  const top    = minsToY(Math.max(startMins, gridStart));
  const bottom = minsToY(Math.min(endMins,   gridEnd));
  const height = Math.max(bottom - top, 24);
  const accentColor = hashColor(show.channel?._id || show.channel);
  const showHandles = height >= 28;

  return (
    <Box sx={{
      position: 'absolute',
      top: top + 1,
      left: 2 + indentLevel * INDENT_PX,
      right: 2,
      height: Math.max(height - 2, 20),
      bgcolor: show.isHidden ? '#3a3a3a' : '#111827', borderRadius: '4px',
      borderLeft: `3px solid ${show.isHidden ? '#666' : accentColor}`,
      boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
      opacity: isDragging ? 0.2 : show.isHidden ? 0.45 : 1,
      transition: 'opacity 0.08s',
      zIndex: isDragging ? 0 : 2 + indentLevel,
      userSelect: 'none',
      overflow: 'hidden',
    }}>

      {/* ── Top resize handle ── */}
      {showHandles && (
        <Box
          onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onResizeTopStart(e); }}
          sx={{
            position: 'absolute', top: 0, left: 0, right: 0, height: HANDLE_H,
            cursor: 'n-resize', zIndex: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            '&:hover .grip': { opacity: 1 },
          }}
        >
          <Box className="grip" sx={{
            width: 24, height: 3, borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.55)', opacity: 0.35, transition: 'opacity 0.15s',
          }} />
        </Box>
      )}

      {/* ── Draggable content body ── */}
      <Box
        onMouseDown={(e) => { e.preventDefault(); onMoveStart(e); }}
        onClick={(e) => { e.stopPropagation(); onClick?.(); }}
        sx={{
          position: 'absolute',
          top: showHandles ? HANDLE_H : 0,
          bottom: showHandles ? HANDLE_H : 0,
          left: 0, right: 0,
          p: '2px 6px',
          cursor: 'grab',
          overflow: 'hidden',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
          {show.iconImage && (
            <Avatar src={show.iconImage} sx={{ width: 16, height: 16, flexShrink: 0 }} />
          )}
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#fff', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {show.title}
          </Typography>
        </Box>
        {height > 32 && (
          <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', lineHeight: 1.2 }}>
            {formatTime(show.timeStart)}–{formatTime(show.timeEnd)}
          </Typography>
        )}
      </Box>

      {/* ── Bottom resize handle ── */}
      {showHandles && (
        <Box
          onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onResizeBottomStart(e); }}
          sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: HANDLE_H,
            cursor: 's-resize', zIndex: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            '&:hover .grip': { opacity: 1 },
          }}
        >
          <Box className="grip" sx={{
            width: 24, height: 3, borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.55)', opacity: 0.35, transition: 'opacity 0.15s',
          }} />
        </Box>
      )}
    </Box>
  );
}

// ─── Form fields ──────────────────────────────────────────────────────────────
const CLOUDINARY_URL = 'https://api.cloudinary.com/v1_1/deplbr8mp/image/upload';
const UPLOAD_PRESET  = 'jamsap-upload';

const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('upload_preset', UPLOAD_PRESET);
  const res  = await fetch(CLOUDINARY_URL, { method: 'POST', body: fd });
  const data = await res.json();
  return data.url;
};

const DEFAULT_FORM = {
  channel: '', title: '', description: '',
  bannerImage: '', iconImage: '', showUrl: '',
  timeStart: '08:00', timeEnd: '09:00', dayOfWeek: 'Monday',
  recurrence: { type: 'once', weeks: 1, days: [] },
  specialDate: '', specialEndDate: '',
  isHidden: false,
};

const formToPayload = (form) => ({ ...form, channel: form.channel?._id || form.channel });

function ShowFormFields({ form, setForm }) {
  const [uploading,     setUploading]     = useState(null);
  const [dragOverField, setDragOverField] = useState(null);
  const [channels,      setChannels]      = useState([]);

  useEffect(() => {
    getChannelsV2Api().then(({ data }) => {
      setChannels(data);
      if (data.length > 0) {
        setForm((f) => (f.channel ? f : { ...f, channel: data[0]._id }));
      }
    }).catch(() => {});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const uploadFile = async (field, file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(field);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, [field]: url }));
    } catch { } finally { setUploading(null); }
  };

  const handleFileInput = (field, e) => uploadFile(field, e.target.files[0]);

  const handleDrop = (field, e) => {
    e.preventDefault();
    setDragOverField(null);
    uploadFile(field, e.dataTransfer.files[0]);
  };

  const dropZoneProps = (field) => ({
    onDragOver:  (e) => { e.preventDefault(); setDragOverField(field); },
    onDragEnter: (e) => { e.preventDefault(); setDragOverField(field); },
    onDragLeave: ()  => setDragOverField(null),
    onDrop:      (e) => handleDrop(field, e),
    onClick:     ()  => document.getElementById(`show-${field}-upload`).click(),
  });

  const recType      = form.recurrence?.type || 'weekly';
  const channelId    = form.channel?._id || form.channel || '';
  const selectedDays = form.recurrence?.days || [];

  const toggleDay = (day) => {
    const next = selectedDays.includes(day)
      ? selectedDays.filter((d) => d !== day)
      : [...selectedDays, day];
    setForm((f) => ({ ...f, recurrence: { ...f.recurrence, days: next } }));
  };

  return (
    <>
      {/* Channel */}
      <TextField select label="Channel" value={channelId} required size="small" fullWidth
        onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}>
        {channels.map((c) => <MenuItem key={c._id} value={c._id}>{c.title}</MenuItem>)}
      </TextField>

      {/* Icon (left) + Title & Description (right) */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch' }}>
        {/* Icon drop zone */}
        <Box>
          {/* <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.5}>
            Icon
          </Typography> */}
          <Box
            {...dropZoneProps('iconImage')}
            sx={{
              position: 'relative', width: 110, height: 110, borderRadius: 1, overflow: 'hidden',
              border: '2px dashed',
              borderColor: dragOverField === 'iconImage' ? 'primary.main' : 'divider',
              bgcolor: dragOverField === 'iconImage' ? 'action.hover' : 'background.default',
              cursor: 'pointer', transition: 'border-color 0.15s, background-color 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {form.iconImage
              ? <Box component="img" src={form.iconImage} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              : (
                <Box sx={{ textAlign: 'center', color: 'text.disabled', pointerEvents: 'none' }}>
                  <ImageIcon sx={{ fontSize: 22 }} />
                  <Typography variant="caption" display="block" sx={{ fontSize: 9 }}>Drop here</Typography>
                </Box>
              )
            }
            {uploading === 'iconImage' && (
              <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              </Box>
            )}
          </Box>
          <input id="show-iconImage-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleFileInput('iconImage', e)} />
        </Box>

        {/* Title + Description stacked */}
        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <TextField label="Title" value={form.title} required size="small" fullWidth
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          <TextField label="Description" value={form.description} size="small" multiline rows={2} fullWidth
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
        </Stack>
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField label="Time Start" value={form.timeStart} size="small" type="time" fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => setForm((f) => ({ ...f, timeStart: e.target.value }))} />
        <TextField label="Time End" value={form.timeEnd} size="small" type="time" fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => setForm((f) => ({ ...f, timeEnd: e.target.value }))} />
      </Box>

      <TextField select label="Recurrence" value={recType} size="small" fullWidth
        onChange={(e) => setForm((f) => ({ ...f, recurrence: { ...f.recurrence, type: e.target.value } }))}>
        {RECURRENCE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
      </TextField>

      {recType !== 'daily' && recType !== 'special' && (
        <TextField select label="Day of Week" value={form.dayOfWeek || 'Monday'} size="small" fullWidth
          onChange={(e) => setForm((f) => ({ ...f, dayOfWeek: e.target.value }))}>
          {DAYS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
        </TextField>
      )}

      {(recType === 'weekly' || recType === 'biweekly') && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Days (overrides Day of Week if set)
          </Typography>
          <FormGroup row>
            {DAYS.map((day) => (
              <FormControlLabel key={day}
                control={<Checkbox size="small" checked={selectedDays.includes(day)} onChange={() => toggleDay(day)} />}
                label={day.slice(0, 3)} />
            ))}
          </FormGroup>
        </Box>
      )}

      {recType === 'special' && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Special Date" value={form.specialDate || ''} size="small" type="date" fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => setForm((f) => ({ ...f, specialDate: e.target.value }))} />
          <TextField label="End Date" value={form.specialEndDate || ''} size="small" type="date" fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => setForm((f) => ({ ...f, specialEndDate: e.target.value }))} />
        </Box>
      )}

    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Shows() {
  const { user } = useAuth();
  const canWrite = !user?.adminAccess?.shows || user.adminAccess.shows.write === true;

  const [shows,      setShows]      = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [viewMode,   setViewMode]   = useState('list');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow,    setEditRow]    = useState(null);
  const [form,       setForm]       = useState(DEFAULT_FORM);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');

  // drag state — stored in refs to avoid stale closures; ghost triggers re-render
  const [dragGhost,   setDragGhost]   = useState(null); // { showId, dayIndex, startMins, endMins }
  const dragRef          = useRef(null);  // { type, show, startMins, endMins, duration, clickOffsetMins, dayIndex, ghost }
  const hasDraggedRef    = useRef(false);
  const dragJustEndedRef = useRef(false); // prevents column-click firing right after a drag
  const gridBodyRef      = useRef(null);

  // ─ Data fetching ─
  const fetchShows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getShowsV2Api();
      setShows(data.rows || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchShows(); }, [fetchShows]);

  // ─ Grid position helper ─
  const getGridPos = useCallback((clientX, clientY) => {
    if (!gridBodyRef.current) return null;
    const rect = gridBodyRef.current.getBoundingClientRect();
    const colW  = (rect.width - TIME_COL_W) / 7;
    const dayIndex  = Math.max(0, Math.min(6, Math.floor((clientX - rect.left - TIME_COL_W) / colW)));
    const timeMins  = START_HOUR * 60 + (clientY - rect.top) / PX_PER_MIN;
    return { dayIndex, timeMins };
  }, []);

  // ─ Start drag / resize ─
  const startDrag = useCallback((e, show, type) => {
    if (!canWrite) return;
    e.preventDefault();

    const startMins = timeToMins(show.timeStart);
    let   endMins   = timeToMins(show.timeEnd);
    if (endMins <= startMins) endMins = END_HOUR * 60;
    const duration = endMins - startMins;

    const pos = getGridPos(e.clientX, e.clientY);
    // offset of the click within the card (so card doesn't jump to mouse on move)
    const clickOffsetMins = pos ? Math.max(0, pos.timeMins - startMins) : 0;

    const days = getShowDays(show);
    const dayIndex = Math.max(0, DAYS.indexOf(days[0] || show.dayOfWeek || DAYS[0]));

    hasDraggedRef.current = false;
    dragRef.current = { type, show, startMins, endMins, duration, clickOffsetMins, dayIndex, ghost: null };

    document.body.style.cursor = type === 'move' ? 'grabbing' : 'ns-resize';

    const ghost = { showId: show._id, dayIndex, startMins, endMins };
    dragRef.current.ghost = ghost;
    setDragGhost(ghost);
  }, [canWrite, getGridPos]);

  // ─ Global mouse events ─
  useEffect(() => {
    const onMove = (e) => {
      const dr = dragRef.current;
      if (!dr) return;

      const { type, startMins, endMins, duration, clickOffsetMins, dayIndex: origDayIdx } = dr;
      const pos = getGridPos(e.clientX, e.clientY);
      if (!pos) return;

      hasDraggedRef.current = true;

      // For multi-day shows (daily / recurrence.days > 1) don't change the column
      const newDayIdx = isMultiDay(dr.show) ? origDayIdx : pos.dayIndex;

      let newStart = startMins;
      let newEnd   = endMins;

      if (type === 'move') {
        newStart = snap(clamp(pos.timeMins - clickOffsetMins));
        newEnd   = newStart + duration;
        // keep within grid
        if (newEnd > END_HOUR * 60)   { newEnd = END_HOUR * 60;   newStart = newEnd - duration; }
        if (newStart < START_HOUR * 60) { newStart = START_HOUR * 60; newEnd = newStart + duration; }
      } else if (type === 'resize-top') {
        newStart = snap(clamp(pos.timeMins));
        if (newStart >= endMins - SNAP_MINS) newStart = endMins - SNAP_MINS;
      } else if (type === 'resize-bottom') {
        newEnd = snap(clamp(pos.timeMins));
        if (newEnd <= startMins + SNAP_MINS) newEnd = startMins + SNAP_MINS;
      }

      const ghost = { showId: dr.show._id, dayIndex: newDayIdx, startMins: newStart, endMins: newEnd };
      dragRef.current.ghost = ghost;
      setDragGhost(ghost);
    };

    const onUp = async () => {
      const dr = dragRef.current;
      if (!dr) return;

      dragRef.current = null;
      document.body.style.cursor = '';
      setDragGhost(null);

      // Tiny movement = treat as click (handled by onClick on the card)
      if (!hasDraggedRef.current || !dr.ghost) return;

      // Block column-click that fires right after this mouseup
      dragJustEndedRef.current = true;
      setTimeout(() => { dragJustEndedRef.current = false; }, 50);

      const { showId, dayIndex, startMins, endMins } = dr.ghost;
      const newTimeStart = minsToTimeStr(startMins);
      const newTimeEnd   = minsToTimeStr(endMins);
      const newDay       = DAYS[dayIndex];

      // Optimistic update
      setShows((prev) => prev.map((s) => {
        if (s._id !== showId) return s;
        const updated = { ...s, timeStart: newTimeStart, timeEnd: newTimeEnd };
        if (!isMultiDay(s)) {
          updated.dayOfWeek  = newDay;
          updated.recurrence = { ...s.recurrence, days: [] };
        }
        return updated;
      }));

      // Persist
      const payload = { timeStart: newTimeStart, timeEnd: newTimeEnd };
      if (!isMultiDay(dr.show)) {
        payload.dayOfWeek  = newDay;
        payload.recurrence = { ...(dr.show.recurrence || {}), days: [] };
      }
      try {
        await editShowV2Api(showId, payload);
        fetchShows();
      } catch (err) {
        alert('Failed to save: ' + (err?.response?.data?.error || err.message));
        fetchShows(); // revert
      }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };
  }, [getGridPos, fetchShows]);

  // ─ Column click → create show ─
  const handleColumnClick = useCallback((e, day) => {
    if (!canWrite || dragJustEndedRef.current) return;
    const pos = getGridPos(e.clientX, e.clientY);
    if (!pos) return;
    const startMins = Math.floor(pos.timeMins / SNAP_MINS) * SNAP_MINS;
    const endMins   = Math.min(startMins + 60, END_HOUR * 60);
    setEditRow(null);
    setForm({ ...DEFAULT_FORM, dayOfWeek: day, timeStart: minsToTimeStr(startMins), timeEnd: minsToTimeStr(endMins) });
    setError('');
    setDialogOpen(true);
  }, [canWrite, getGridPos]);

  // ─ CRUD ─
  const openAdd  = () => { setEditRow(null); setForm(DEFAULT_FORM); setError(''); setDialogOpen(true); };
  const openEdit = (show) => { if (dragRef.current) return; setEditRow(show); setForm(show); setError(''); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const payload = formToPayload(form);
      if (editRow) await editShowV2Api(editRow._id, payload);
      else         await addShowV2Api(payload);
      setDialogOpen(false);
      fetchShows();
    } catch (e) { setError(e?.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!editRow || !window.confirm(`Delete "${editRow.title}"?`)) return;
    try { await deleteShowV2Api(editRow._id); setDialogOpen(false); fetchShows(); }
    catch (e) { alert(e?.response?.data?.error || 'Delete failed'); }
  };

  // ─ Group shows by day ─
  const showsByDay = Object.fromEntries(DAYS.map((d) => [d, []]));
  for (const show of shows) {
    for (const day of getShowDays(show)) {
      if (showsByDay[day]) showsByDay[day].push(show);
    }
  }

  return (
    // Fill the viewport below the AppBar (64 px) — grid scrolls, page does not
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2, pb: 1.5, flexShrink: 0 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Shows</Typography>
          <Typography variant="body2" color="text.secondary">Weekly schedule</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {loading && <CircularProgress size={20} />}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => { if (v) setViewMode(v); }}
            size="small"
          >
            <ToggleButton value="schedule"><CalendarMonthIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="list"><ViewListIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
          {canWrite && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd}
              sx={{ bgcolor: '#fec958', color: '#000', '&:hover': { bgcolor: '#f0b830' }, fontWeight: 700 }}>
              Add Show
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Schedule grid ── */}
      {viewMode === 'schedule' && <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', mx: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        {/* Fixed day-of-week headers */}
        <Box sx={{
          display: 'flex', flexShrink: 0,
          bgcolor: 'background.paper',
          borderBottom: '2px solid', borderColor: 'divider',
        }}>
          <Box sx={{ width: TIME_COL_W, flexShrink: 0 }} />
          {DAYS.map((day) => {
            const isToday = day === DAYS[new Date().getDay()];
            return (
              <Box key={day} sx={{
                flex: 1, minWidth: 0, textAlign: 'center', py: 1.25,
                borderLeft: '1px solid', borderColor: 'divider',
                bgcolor: isToday ? 'rgba(254,201,88,0.12)' : 'transparent',
              }}>
                <Typography variant="body2" fontWeight={700}
                  sx={{ color: isToday ? '#fec958' : 'text.primary' }}>
                  {day}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Scrollable: time labels + day columns */}
        <Box ref={gridBodyRef} sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex' }}>

          {/* Time label column */}
          <Box sx={{ width: TIME_COL_W, flexShrink: 0, position: 'relative', height: GRID_HEIGHT }}>
            {HOUR_LINES.map((h) => (
              <Box key={h} sx={{ position: 'absolute', top: (h - START_HOUR) * HOUR_HEIGHT - 9, right: 6, left: 0, textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, lineHeight: 1 }}>
                  {formatHourLabel(h)}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Seven day columns */}
          {DAYS.map((day, dayIndex) => {
            const isToday = day === DAYS[new Date().getDay()];
            return (
            <Box key={day} onClick={(e) => handleColumnClick(e, day)} sx={{
              flex: 1, minWidth: 0, position: 'relative', height: GRID_HEIGHT,
              borderLeft: '1px solid', borderColor: 'divider',
              cursor: canWrite ? 'cell' : 'default',
              bgcolor: isToday ? 'rgba(254,201,88,0.04)' : 'transparent',
            }}>
              {/* Full-hour lines */}
              {HOUR_LINES.map((h) => (
                <Box key={h} sx={{
                  position: 'absolute', top: (h - START_HOUR) * HOUR_HEIGHT, left: 0, right: 0,
                  borderTop: '1px solid',
                  borderColor: h % 6 === 0 ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.06)',
                }} />
              ))}

              {/* Half-hour lines (dashed, lighter) */}
              {HALF_LINES.map((h) => (
                <Box key={h} sx={{
                  position: 'absolute', top: (h - START_HOUR) * HOUR_HEIGHT, left: 8, right: 0,
                  borderTop: '1px dashed rgba(0,0,0,0.04)',
                }} />
              ))}

              {/* Ghost (drop preview) */}
              {dragGhost?.dayIndex === dayIndex && (
                <GhostCard
                  startMins={dragGhost.startMins}
                  endMins={dragGhost.endMins}
                  color={hashColor(shows.find((s) => s._id === dragGhost.showId)?.channel?._id)}
                />
              )}

              {/* Show cards */}
              {(() => {
                const indentMap = computeIndentLevels(showsByDay[day]);
                return showsByDay[day].map((show) => (
                  <ShowCard
                    key={`${show._id}-${day}`}
                    show={show}
                    indentLevel={indentMap.get(show._id) || 0}
                    isDragging={dragGhost?.showId === show._id}
                    onMoveStart={(e)         => startDrag(e, show, 'move')}
                    onResizeTopStart={(e)    => startDrag(e, show, 'resize-top')}
                    onResizeBottomStart={(e) => startDrag(e, show, 'resize-bottom')}
                    onClick={() => { if (!hasDraggedRef.current && canWrite) openEdit(show); }}
                  />
                ));
              })()}
            </Box>
            );
          })}
        </Box>
      </Box>}

      {/* ── List view ── */}
      {viewMode === 'list' && (
        <Box sx={{ flex: 1, minHeight: 0, px: 2, pb: 2, display: 'flex', flexDirection: 'column' }}>
          <DataGrid
            rows={shows.map((s) => ({ ...s, id: s._id }))}
            columns={[
              {
                field: 'iconImage', headerName: '', width: 52, sortable: false,
                renderCell: (p) => <Avatar src={p.value} sx={{ width: 32, height: 32, marginTop: 1 }} />,
              },
              { field: 'title', headerName: 'Title', flex: 1 },
              {
                field: 'channel', headerName: 'Channel', width: 150,
                renderCell: (p) => p.value?.title || '—',
              },
              { field: 'dayOfWeek', headerName: 'Day', width: 110 },
              {
                field: 'timeStart', headerName: 'Time', width: 140,
                renderCell: (p) => `${formatTime(p.value)} – ${formatTime(p.row.timeEnd)}`,
              },
              {
                field: 'recurrence', headerName: 'Recurrence', width: 120,
                renderCell: (p) => <span style={{ textTransform: 'capitalize' }}>{p.value?.type || '—'}</span>,
              },
              {
                field: 'isHidden', headerName: 'Status', width: 110,
                renderCell: (p) => (
                  <Chip
                    label={p.value ? 'Inactive' : 'Active'}
                    size="small"
                    color={p.value ? 'default' : 'success'}
                    variant="outlined"
                  />
                ),
              },
              ...(canWrite ? [{
                field: 'actions', headerName: '', width: 90, sortable: false,
                renderCell: (p) => (
                  <Box>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(p.row); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setEditRow(p.row); setTimeout(handleDelete, 0); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ),
              }] : []),
            ]}
            loading={loading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            sx={{ flex: 1, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e8eaf0' }}
            disableRowSelectionOnClick
          />
        </Box>
      )}

      {/* ── Add / Edit dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
          {editRow ? 'Edit Show' : 'Add Show'}
          <FormControlLabel
            control={
              <Switch
                checked={!form.isHidden}
                onChange={(e) => setForm((f) => ({ ...f, isHidden: !e.target.checked }))}
                size="small"
              />
            }
            label={<Typography variant="body2" fontWeight={600}>{form.isHidden ? 'Inactive' : 'Active'}</Typography>}
            labelPlacement="start"
            sx={{ m: 0, gap: 0.5 }}
          />
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <ShowFormFields form={form} setForm={setForm} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          {editRow
            ? <Button color="error" onClick={handleDelete} startIcon={<DeleteIcon />}>Delete</Button>
            : <span />}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}
              sx={{ bgcolor: '#fec958', color: '#000', '&:hover': { bgcolor: '#f0b830' }, fontWeight: 700 }}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
